import React, { Component, ErrorInfo } from 'react'
import ReactModal from 'react-modal'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { logService } from 'services'
import { createWindowSelectionsSelector } from 'selectors/selectionSelectors'
import { WindowSelections } from 'store/selection/selectionTypes'
import * as constants from 'view/styles/constants'
import { clientActionLogToServer, ClientActionLogToServerArgs, ClientLogStream, LogSeverity } from 'api'
import { Locale, Translation } from 'locale'
import ErrorScreen from './ErrorScreen'
import { createActionLogSelector } from 'selectors/actionLogSelector'
import { ActionLog } from 'store/system'

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  message: string
  stack: string
  showModal: boolean
}

export interface ErrorBoundaryProps {
  currentSelections: WindowSelections
  actionLog: ActionLog
  onPersistError: (logErrorArgs: ClientActionLogToServerArgs) => unknown
  children?: React.ReactNode
}

const mapStateToProps = createStructuredSelector({
  currentSelections: createWindowSelectionsSelector(),
  actionLog: createActionLogSelector()
})

const mapDispatchToProps = {
  onPersistError: clientActionLogToServer
}

export class ErrorBoundaryComponent extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state = {
    hasError: false,
    error: null,
    message: '',
    stack: '',
    showModal: false
  }

  private setError = (evt: ErrorEvent) => {
    const { onPersistError, currentSelections, actionLog } = this.props
    const message = evt.message || Locale.translate(Translation.SC_ERROR_CAUGHT)
    const error: Error = evt.error || new Error(message)

    // This prevents the error being logged twice - here and in componentDidCatch
    if (error.stack && error.stack.indexOf('invokeGuardedCallbackDev') >= 0) {
      return
    }

    const stack = evt.error ? evt.error.stack : `File: ${evt.filename} - Line: ${evt.lineno} - Column: ${evt.colno}`
    this.setState({ hasError: true, error, message, stack, showModal: true })
    logService.logErrors(message, stack, currentSelections, actionLog)
    onPersistError({
      logSeverity: LogSeverity.ERROR,
      message,
      debugInfo: {
        stack,
        selections: JSON.stringify(currentSelections),
        actionLog: JSON.stringify(actionLog)
      },
      logStream: ClientLogStream.GLOBAL
    })

    evt.preventDefault()
  }

  private setPromiseError = (evt: PromiseRejectionEvent) => {
    const { onPersistError, currentSelections, actionLog } = this.props
    const message = evt.reason || Locale.translate(Translation.SC_ERROR_CAUGHT)
    const error = new Error(message)
    // Currently not possible to tell where event originated from
    const stack = 'Not Available'

    this.setState({ hasError: true, error, message, stack, showModal: true })
    logService.logErrors(message, stack, currentSelections, actionLog)
    onPersistError({
      logSeverity: LogSeverity.ERROR,
      message,
      debugInfo: {
        stack,
        selections: JSON.stringify(currentSelections),
        actionLog: JSON.stringify(actionLog)
      },
      logStream: ClientLogStream.GLOBAL
    })

    evt.preventDefault()
  }

  static getDerivedStateFromError(error: Error) {
    const message = error.toString() || Locale.translate(Translation.SC_ERROR_CAUGHT)
    const stack = error.stack || 'Not Available'
    return { hasError: true, error, message, stack, showModal: false }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onPersistError, currentSelections, actionLog } = this.props
    const message = error.toString() || Locale.translate(Translation.SC_ERROR_CAUGHT)
    const stack = error.stack || errorInfo.componentStack || ''

    logService.logErrors(message, stack, currentSelections, actionLog)
    onPersistError({
      logSeverity: LogSeverity.ERROR,
      message,
      debugInfo: {
        stack,
        selections: JSON.stringify(currentSelections),
        actionLog: JSON.stringify(actionLog)
      },
      logStream: ClientLogStream.GLOBAL
    })
  }

  componentDidMount() {
    window.addEventListener('error', this.setError)
    window.addEventListener('unhandledrejection', this.setPromiseError)
  }

  componentWillUnmount() {
    window.removeEventListener('error', this.setError)
    window.removeEventListener('unhandledrejection', this.setPromiseError)
  }

  private closeModal = () => {
    this.setState({ hasError: false, showModal: false })
  }

  private renderError = () => {
    const { error, message, stack, showModal } = this.state
    return showModal ? (
      this.props.children
    ) : (
      <ErrorScreen error={error} message={message} stack={stack} selections={this.props.currentSelections} closeApp={true} />
    )
  }

  render() {
    const { hasError, error, message, stack, showModal } = this.state

    return (
      <>
        <ReactModal
          style={{ overlay: { zIndex: constants.zIndexModalOverlay } }}
          ariaHideApp={false}
          isOpen={showModal}
          overlayClassName="modal-overlay"
          className="modal-overlay-content"
          shouldCloseOnEsc={true}
          shouldCloseOnOverlayClick={false}
          onRequestClose={this.closeModal}>
          <ErrorScreen error={error} message={message} stack={stack} selections={this.props.currentSelections} onClose={this.closeModal} />
        </ReactModal>
        {hasError ? this.renderError() : this.props.children}
      </>
    )
  }
}

export const ErrorBoundary = connect(mapStateToProps, mapDispatchToProps)(ErrorBoundaryComponent)
